import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, Mail, Phone, MapPin } from "lucide-react";

export function AdminLogin() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/admin");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="admin-login">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-3xl p-8 shadow-sm">
        <div className="section-eyebrow">Al Khidmaat</div>
        <h1 className="mt-2 font-display text-3xl font-semibold text-ink">{t("admin.login_title")}</h1>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="al-email">{t("admin.email")}</Label>
            <Input id="al-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1.5 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="al-pass">{t("admin.password")}</Label>
            <Input id="al-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-1.5 rounded-xl" />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-sage-900 hover:bg-sage-700 text-bone h-12">
            {loading ? <><Loader2 className="me-2 h-4 w-4 animate-spin" />…</> : t("admin.login")}
          </Button>
        </form>
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [tab, setTab] = useState("inquiries");

  useEffect(() => {
    if (user === false) nav("/admin/login");
  }, [user, nav]);

  const reload = async () => {
    try {
      const [s, iq] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
        axios.get(`${API}/admin/inquiries`, { headers: authHeaders() }),
      ]);
      setStats(s.data);
      setInquiries(iq.data);
    } catch (e) {
      // Ignore
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") reload();
    // eslint-disable-next-line
  }, [user]);

  if (user === null) return <div className="py-32 text-center text-stone-500">Loading…</div>;
  if (!user) return null;

  const updStatus = async (id, status) => {
    await axios.patch(`${API}/admin/inquiries/${id}?status=${status}`, {}, { headers: authHeaders() });
    toast.success("Updated");
    reload();
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      <div className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <div className="font-display text-lg font-semibold text-ink">{t("admin.dashboard")}</div>
          <div className="ms-auto flex items-center gap-3">
            <div className="text-sm text-stone-600">{user.email}</div>
            <Button onClick={() => { logout(); nav("/"); }} variant="outline" className="rounded-full" data-testid="admin-logout"><LogOut className="me-2 h-4 w-4" />{t("admin.logout")}</Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              ["New Inquiries", stats.new_inquiries],
              ["Total Inquiries", stats.inquiries],
              ["Hospitals", stats.hospitals],
              ["Doctors", stats.doctors],
            ].map(([l, v], i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`stat-${i}`}>
                <div className="text-xs uppercase tracking-widest text-stone-500">{l}</div>
                <div className="mt-2 font-display text-3xl text-sage-900">{v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden">
          <div className="border-b border-stone-200 p-4 flex gap-2">
            {[["inquiries", t("admin.inquiries")]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-full text-sm font-medium ${tab === k ? "bg-sage-900 text-bone" : "text-stone-600 hover:bg-stone-100"}`}>{l}</button>
            ))}
          </div>

          {tab === "inquiries" && (
            <div className="divide-y divide-stone-200">
              {inquiries.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">No inquiries yet.</div>}
              {inquiries.map((iq) => (
                <div key={iq.id} className="p-6" data-testid={`inquiry-row-${iq.id}`}>
                  <div className="flex flex-wrap items-start gap-4 justify-between">
                    <div>
                      <div className="font-display font-semibold text-ink">{iq.name}</div>
                      <div className="mt-1 text-xs text-stone-500">{new Date(iq.created_at).toLocaleString()}</div>
                      <div className="mt-3 flex flex-wrap gap-4 text-sm text-stone-700">
                        <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-ochre-500" />{iq.email}</span>
                        <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-ochre-500" />{iq.phone}</span>
                        {iq.country && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-ochre-500" />{iq.country}</span>}
                      </div>
                      {iq.treatment && <div className="mt-2 text-sm"><span className="text-stone-500">Treatment:</span> {iq.treatment}</div>}
                      {iq.message && <p className="mt-2 text-sm text-stone-700 max-w-2xl">{iq.message}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-3 py-1 rounded-full ${iq.status === "new" ? "bg-ochre-500/15 text-ochre-600" : "bg-sage-100 text-sage-900"}`}>{iq.status}</span>
                      <select value={iq.status} onChange={(e) => updStatus(iq.id, e.target.value)} className="text-xs rounded-full border border-stone-200 px-3 py-1.5 bg-white">
                        <option value="new">new</option>
                        <option value="contacted">contacted</option>
                        <option value="closed">closed</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
